import { useState } from "react";
import { Box, Button, HStack, Spinner, Text, VStack } from "@chakra-ui/react";
import { HiPaperClip } from "react-icons/hi2";
import { Field, FormInput, FormTextarea, FormSwitch } from "@/components/jobOffer/ui/inputs";
import { SectionCard, SectionHeader, Divider, Grid2, Grid3, SubsectionTitle } from "@/components/jobOffer/ui/layout";
import { AddButton, RemoveButton, ItemHeader, RepeatableText } from "@/components/jobOffer/ui/listHelpers";
import { toaster } from "@/components/ui/toaster";
import { extractBusinessErrorMessage } from "@/services/apiErrorMessage";
import { CampaignService } from "@/services/CampaignService";

const attachmentUrl = (path) =>
    /^https?:\/\//i.test(path)
        ? path // legacy attachment (pre-task-31 "List<string> URL" model) -- Path holds the original external link, not one of our storage object keys
        : `${process.env.REACT_APP_BASE_URL || "/mlapi"}/campaign/attachments/file?name=${encodeURIComponent(path)}`;

const ProductAttachments = ({ attachments, onChange, uploadInputId }) => {
    const [isUploading, setIsUploading] = useState(false);

    const handleFilesSelected = async (e) => {
        const files = Array.from(e.target.files ?? []);
        e.target.value = "";
        if (!files.length) return;
        setIsUploading(true);
        try {
            const uploaded = await CampaignService.uploadProductAttachments(files);
            onChange([...(attachments ?? []), ...(uploaded ?? [])]);
        } catch (error) {
            toaster.create({ type: "error", title: extractBusinessErrorMessage(error) ?? "Nie udało się wgrać załącznika" });
        } finally {
            setIsUploading(false);
        }
    };

    const remove = (i) => onChange((attachments ?? []).filter((_, idx) => idx !== i));

    return (
        <Box>
            <HStack justify="space-between" mb={1.5}>
                <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider">Załączniki</Text>
                <Button as="label" htmlFor={uploadInputId} variant="outlined" size="xs" disabled={isUploading} cursor="pointer">
                    {isUploading ? <Spinner size="xs" /> : <HiPaperClip />}
                    Dodaj plik
                    <input id={uploadInputId} type="file" multiple style={{ display: "none" }} onChange={handleFilesSelected} />
                </Button>
            </HStack>
            <VStack align="stretch" gap={2}>
                {(attachments ?? []).map((att, i) => (
                    <HStack key={i} gap={2}>
                        <Text fontSize="sm" color="text.white" flex={1} truncate>{att.name}</Text>
                        {att.path && (
                            <Box as="a" href={attachmentUrl(att.path)} target="_blank" rel="noreferrer" fontSize="xs" color="brand.400">Otwórz</Box>
                        )}
                        <RemoveButton onClick={() => remove(i)} />
                    </HStack>
                ))}
                {(attachments ?? []).length === 0 && <Text fontSize="sm" color="text.grey">Brak załączników</Text>}
            </VStack>
        </Box>
    );
};

const newProduct = () => ({
    id: crypto.randomUUID(), name: "", category: "", shortDescription: "", fullDescription: "",
    manufacturer: "", brand: "", model: "", variant: "",
    applications: [], benefits: [], usp: [], parameters: {},
    price: { price: null, currency: "PLN", priceFrom: null, priceTo: null, pricingMethod: "", subscription: false, oneTimeFee: null, implementationCost: null, additionalCosts: [], discounts: [], promoConditions: "" },
    availability: { isAvailable: true, leadTime: "", deliveryTime: "", localAvailability: "", limitedQuantity: false, serviceStartDate: null },
    usageInstructions: "",
    safetyInfo: { contraindications: [], warnings: [], hazards: [], requiredProtection: [], ageRestrictions: "", usageRestrictions: [] },
    attachments: [], faq: [],
});

const ParamsEditor = ({ parameters, onChange }) => {
    const entries = Object.entries(parameters ?? {});
    const update = (i, key, val) => {
        const next = [...entries];
        next[i] = [key, val];
        onChange(Object.fromEntries(next));
    };
    const remove = (i) => onChange(Object.fromEntries(entries.filter((_, idx) => idx !== i)));
    const add = () => onChange({ ...parameters, "": "" });

    return (
        <Box>
            <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={1.5}>Parametry</Text>
            <VStack align="stretch" gap={2}>
                {entries.map(([key, val], i) => (
                    <HStack key={i} gap={2}>
                        <FormInput value={key} onChange={(e) => update(i, e.target.value, val)} placeholder="Nazwa parametru" flex={1} />
                        <FormInput value={val} onChange={(e) => update(i, key, e.target.value)} placeholder="Wartość" flex={1} />
                        <RemoveButton onClick={() => remove(i)} />
                    </HStack>
                ))}
                <AddButton label="Dodaj parametr" onClick={add} />
            </VStack>
        </Box>
    );
};

const FaqEditor = ({ faq, onChange }) => {
    const update = (i, patch) => onChange(faq.map((f, idx) => idx === i ? { ...f, ...patch } : f));
    const remove = (i) => onChange(faq.filter((_, idx) => idx !== i));
    const add = () => onChange([...faq, { question: "", answer: "" }]);

    return (
        <Box>
            <Text fontSize="xs" fontWeight={600} color="text.grey" textTransform="uppercase" letterSpacing="wider" mb={1.5}>FAQ</Text>
            <VStack align="stretch" gap={2}>
                {faq.map((f, i) => (
                    <Box key={i} borderWidth={1} borderColor="border.primary" borderRadius={8} p={3}>
                        <HStack gap={2} align="flex-start">
                            <VStack flex={1} gap={1.5}>
                                <Text fontSize="xs" fontWeight={600} color="text.grey">{`FAQ #${i + 1}`}</Text>
                                <Field label="Pytanie"><FormInput value={f.question} onChange={(e) => update(i, { question: e.target.value })} placeholder="Pytanie" /></Field>
                                <Field label="Odpowiedź"><FormTextarea value={f.answer} onChange={(e) => update(i, { answer: e.target.value })} placeholder="Odpowiedź" rows={2} /></Field>
                            </VStack>
                            <RemoveButton onClick={() => remove(i)} />
                        </HStack>
                    </Box>
                ))}
                <AddButton label="Dodaj pytanie FAQ" onClick={add} />
            </VStack>
        </Box>
    );
};

const StepProducts = ({ values, onChange }) => {
    const products = values.products ?? [];
    const setProducts = (next) => onChange({ ...values, products: next });
    const updateProduct = (i, patch) => setProducts(products.map((p, idx) => idx === i ? { ...p, ...patch } : p));
    const updateNested = (i, section, patch) => updateProduct(i, { [section]: { ...products[i][section], ...patch } });
    const addProduct = () => setProducts([...products, newProduct()]);
    const removeProduct = (i) => setProducts(products.filter((_, idx) => idx !== i));

    return (
        <SectionCard>
            <SectionHeader number={3} title="Produkty i usługi" subtitle="Dowolna liczba produktów lub usług w bazie wiedzy kampanii" />
            <VStack align="stretch" gap={4}>
                {products.map((p, i) => (
                    <Box key={p.id ?? i} borderWidth={1} borderColor="border.primary" borderRadius={8} p={4}>
                        <ItemHeader title={p.name || "Nowy produkt"} index={i} onRemove={() => removeProduct(i)} />
                        <VStack align="stretch" gap={3}>
                            <Grid2>
                                <Field label="Nazwa" required><FormInput value={p.name} onChange={(e) => updateProduct(i, { name: e.target.value })} /></Field>
                                <Field label="Kategoria"><FormInput value={p.category ?? ""} onChange={(e) => updateProduct(i, { category: e.target.value })} /></Field>
                            </Grid2>
                            <Field label="Krótki opis"><FormTextarea value={p.shortDescription ?? ""} onChange={(e) => updateProduct(i, { shortDescription: e.target.value })} rows={2} /></Field>
                            <Field label="Pełny opis"><FormTextarea value={p.fullDescription ?? ""} onChange={(e) => updateProduct(i, { fullDescription: e.target.value })} rows={3} /></Field>
                            <Grid3>
                                <Field label="Producent"><FormInput value={p.manufacturer ?? ""} onChange={(e) => updateProduct(i, { manufacturer: e.target.value })} /></Field>
                                <Field label="Marka"><FormInput value={p.brand ?? ""} onChange={(e) => updateProduct(i, { brand: e.target.value })} /></Field>
                                <Field label="Model"><FormInput value={p.model ?? ""} onChange={(e) => updateProduct(i, { model: e.target.value })} /></Field>
                            </Grid3>
                            <Grid2>
                                <Field label="Wariant"><FormInput value={p.variant ?? ""} onChange={(e) => updateProduct(i, { variant: e.target.value })} /></Field>
                            </Grid2>

                            <Divider my={2} />
                            <SubsectionTitle>Zastosowanie i korzyści</SubsectionTitle>
                            <Grid3>
                                <RepeatableText label="Zastosowanie" values={p.applications ?? []} onChange={(v) => updateProduct(i, { applications: v })} placeholder="Zastosowanie" />
                                <RepeatableText label="Korzyści" values={p.benefits ?? []} onChange={(v) => updateProduct(i, { benefits: v })} placeholder="Korzyść" />
                                <RepeatableText label="USP" values={p.usp ?? []} onChange={(v) => updateProduct(i, { usp: v })} placeholder="USP" />
                            </Grid3>
                            <ParamsEditor parameters={p.parameters} onChange={(v) => updateProduct(i, { parameters: v })} />

                            <Divider my={2} />
                            <SubsectionTitle>Cena</SubsectionTitle>
                            <Grid3>
                                <Field label="Cena"><FormInput type="number" value={p.price?.price ?? ""} onChange={(e) => updateNested(i, "price", { price: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                                <Field label="Waluta"><FormInput value={p.price?.currency ?? ""} onChange={(e) => updateNested(i, "price", { currency: e.target.value })} /></Field>
                                <Field label="Sposób wyceny"><FormInput value={p.price?.pricingMethod ?? ""} onChange={(e) => updateNested(i, "price", { pricingMethod: e.target.value })} /></Field>
                            </Grid3>
                            <Grid3>
                                <Field label="Cena od"><FormInput type="number" value={p.price?.priceFrom ?? ""} onChange={(e) => updateNested(i, "price", { priceFrom: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                                <Field label="Cena do"><FormInput type="number" value={p.price?.priceTo ?? ""} onChange={(e) => updateNested(i, "price", { priceTo: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                                <Field label="Koszt wdrożenia"><FormInput type="number" value={p.price?.implementationCost ?? ""} onChange={(e) => updateNested(i, "price", { implementationCost: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                            </Grid3>
                            <Grid2>
                                <Field label="Opłata jednorazowa"><FormInput type="number" value={p.price?.oneTimeFee ?? ""} onChange={(e) => updateNested(i, "price", { oneTimeFee: e.target.value === "" ? null : Number(e.target.value) })} /></Field>
                            </Grid2>
                            <FormSwitch label="Model abonamentowy" checked={!!p.price?.subscription} onChange={(v) => updateNested(i, "price", { subscription: v })} />
                            <Grid2>
                                <RepeatableText label="Rabaty / promocje" values={p.price?.discounts ?? []} onChange={(v) => updateNested(i, "price", { discounts: v })} placeholder="Rabat" />
                                <RepeatableText label="Dodatkowe koszty" values={p.price?.additionalCosts ?? []} onChange={(v) => updateNested(i, "price", { additionalCosts: v })} placeholder="Dodatkowy koszt" />
                            </Grid2>
                            <Field label="Warunki uzyskania promocji"><FormTextarea value={p.price?.promoConditions ?? ""} onChange={(e) => updateNested(i, "price", { promoConditions: e.target.value })} rows={2} /></Field>

                            <Divider my={2} />
                            <SubsectionTitle>Dostępność</SubsectionTitle>
                            <Grid2>
                                <FormSwitch label="Dostępny" checked={p.availability?.isAvailable ?? true} onChange={(v) => updateNested(i, "availability", { isAvailable: v })} />
                                <FormSwitch label="Ograniczona liczba" checked={!!p.availability?.limitedQuantity} onChange={(v) => updateNested(i, "availability", { limitedQuantity: v })} />
                            </Grid2>
                            <Grid3>
                                <Field label="Termin realizacji"><FormInput value={p.availability?.leadTime ?? ""} onChange={(e) => updateNested(i, "availability", { leadTime: e.target.value })} /></Field>
                                <Field label="Czas dostawy"><FormInput value={p.availability?.deliveryTime ?? ""} onChange={(e) => updateNested(i, "availability", { deliveryTime: e.target.value })} /></Field>
                                <Field label="Dostępność lokalna"><FormInput value={p.availability?.localAvailability ?? ""} onChange={(e) => updateNested(i, "availability", { localAvailability: e.target.value })} /></Field>
                            </Grid3>
                            <Grid2>
                                <Field label="Termin rozpoczęcia usługi"><FormInput type="date" value={p.availability?.serviceStartDate ?? ""} onChange={(e) => updateNested(i, "availability", { serviceStartDate: e.target.value || null })} /></Field>
                            </Grid2>

                            <Divider my={2} />
                            <SubsectionTitle>Sposób korzystania i bezpieczeństwo</SubsectionTitle>
                            <Field label="Instrukcja użytkowania"><FormTextarea value={p.usageInstructions ?? ""} onChange={(e) => updateProduct(i, { usageInstructions: e.target.value })} rows={2} /></Field>
                            <Grid2>
                                <RepeatableText label="Przeciwwskazania" values={p.safetyInfo?.contraindications ?? []} onChange={(v) => updateNested(i, "safetyInfo", { contraindications: v })} placeholder="Przeciwwskazanie" />
                                <RepeatableText label="Ostrzeżenia" values={p.safetyInfo?.warnings ?? []} onChange={(v) => updateNested(i, "safetyInfo", { warnings: v })} placeholder="Ostrzeżenie" />
                            </Grid2>
                            <Grid2>
                                <RepeatableText label="Niebezpieczeństwa" values={p.safetyInfo?.hazards ?? []} onChange={(v) => updateNested(i, "safetyInfo", { hazards: v })} placeholder="Niebezpieczeństwo" />
                                <RepeatableText label="Wymagane środki ochronne" values={p.safetyInfo?.requiredProtection ?? []} onChange={(v) => updateNested(i, "safetyInfo", { requiredProtection: v })} placeholder="Środek ochronny" />
                            </Grid2>
                            <Grid2>
                                <Field label="Ograniczenia wiekowe"><FormInput value={p.safetyInfo?.ageRestrictions ?? ""} onChange={(e) => updateNested(i, "safetyInfo", { ageRestrictions: e.target.value })} placeholder="np. 18+" /></Field>
                            </Grid2>
                            <RepeatableText label="Ograniczenia zastosowania" values={p.safetyInfo?.usageRestrictions ?? []} onChange={(v) => updateNested(i, "safetyInfo", { usageRestrictions: v })} placeholder="Ograniczenie" />

                            <Divider my={2} />
                            <ProductAttachments attachments={p.attachments ?? []} onChange={(v) => updateProduct(i, { attachments: v })} uploadInputId={`product-attachment-input-${p.id ?? i}`} />
                            <FaqEditor faq={p.faq ?? []} onChange={(v) => updateProduct(i, { faq: v })} />
                        </VStack>
                    </Box>
                ))}
                <AddButton label="Dodaj produkt/usługę" onClick={addProduct} />
            </VStack>
        </SectionCard>
    );
};

export default StepProducts;
